import React from "react";
import { useTranslation } from "react-i18next";
import { getMenuItemsBySubmenu } from "../data/menuData";
import {
  getSubmenuLabel,
  getSubmenuAddOns,
  getSubmenuInfo,
} from "../data/menuConfig";
import {
  getMenuItemWithRecipe,
  MenuItemWithRecipe,
} from "../utils/menuHelpers";
import SubmenuTitle from "./SubmenuTitle";

interface MenuDisplayProps {
  location: "montreal" | "delson";
  section: string;
  submenus: string[];
  language?: "en" | "fr";
}

const MenuDisplay: React.FC<MenuDisplayProps> = ({
  location,
  section,
  submenus,
  language,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = language || (i18n.language as "en" | "fr") || "fr";

  // Get the display name for a menu item based on current language
  const getItemName = (item: MenuItemWithRecipe): string => {
    if (currentLang === "fr" && item.nameFr) {
      return item.nameFr;
    }
    return item.name;
  };

  // Get the description for a menu item based on current language
  const getItemDescription = (item: MenuItemWithRecipe): string => {
    if (currentLang === "fr" && item.descriptionFr) {
      return item.descriptionFr;
    }
    return item.description;
  };

  return (
    <>
      {submenus.map((submenu) => {
        const rawItems = getMenuItemsBySubmenu(submenu, location);
        const items = rawItems.map(getMenuItemWithRecipe);
        if (items.length === 0) return null;

        const addOns = getSubmenuAddOns(location, section, submenu);
        const submenuInfoData = getSubmenuInfo(location, section, submenu);

        return (
          <div key={submenu} className="menu-submenu-section">
            <SubmenuTitle>
              {getSubmenuLabel(submenu, currentLang).toUpperCase()}
            </SubmenuTitle>

            {/* Combined info and add-ons banner */}
            {(submenuInfoData || (addOns && addOns.length > 0)) && (
              <div className="menu-submenu-notice">
                <svg
                  className="menu-submenu-notice-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M8 4V4.5M8 7V12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="menu-submenu-notice-content">
                  {submenuInfoData && (
                    <div className="menu-submenu-notice-text">
                      {currentLang === "fr"
                        ? submenuInfoData.infoFr
                        : submenuInfoData.info}
                    </div>
                  )}
                  {addOns && addOns.length > 0 && (
                    <div className="menu-submenu-addons-line">
                      <span className="menu-addons-label">
                        {currentLang === "fr" ? "Accompagnements:" : "Add-ons:"}
                      </span>
                      {addOns.map((addon, index) => (
                        <React.Fragment key={index}>
                          {index > 0 && (
                            <span className="menu-addon-separator"> • </span>
                          )}
                          <span className="menu-addon-item">
                            {currentLang === "fr" ? addon.nameFr : addon.name}{" "}
                            +${addon.price.toFixed(2)}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="menu-items-list">
              {items.map((item) => {
                const itemName = getItemName(item);
                return (
                  <div key={item.id} className="menu-list-item">
                    <div className="menu-list-item-header">
                      <h4 className="menu-list-item-name">
                        {itemName?.toUpperCase() || ""}
                      </h4>
                      <span className="menu-list-item-price">
                        ${item.price.toFixed(2)}
                      </span>
                    </div>
                    <p className="menu-list-item-desc">
                      {getItemDescription(item)}
                    </p>
                    {item.popular && (
                      <span className="menu-item-badge popular">
                        {t("common.popular")}
                      </span>
                    )}
                    {item.spicy && (
                      <span className="menu-item-badge spicy">
                        {t("common.spicy")} 🌶️
                      </span>
                    )}
                    {item.vegetarian && (
                      <span className="menu-item-badge vegetarian">
                        {t("common.vegetarian")} 🌱
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default MenuDisplay;
